import type { ComponentPropsWithoutRef, FC, ReactNode } from 'react'

import Link from 'next/link'
import { cn } from '@/lib/utils'

export type ButtonVariant = 'primary' | 'secondary' | 'ghostDark' | 'outline'
export type ButtonSize = 'md' | 'lg'

interface ButtonVariantOptions {
    variant?: ButtonVariant
    size?: ButtonSize
    className?: string
}

interface ButtonLinkProps extends ComponentPropsWithoutRef<typeof Link> {
    variant?: ButtonVariant
    size?: ButtonSize
}

interface ButtonProps extends ComponentPropsWithoutRef<'button'> {
    variant?: ButtonVariant
    size?: ButtonSize
}

// Shared button styling. Signal (primary) is reserved for the one main action per view.
export const buttonVariants = ({ variant = 'primary', size = 'md', className }: ButtonVariantOptions = {}): string => {
    return cn(
        'inline-flex select-none items-center justify-center gap-2 rounded-full font-medium whitespace-nowrap',
        'transition-[background-color,border-color,color,transform] duration-200 ease-out active:translate-y-px',
        'disabled:pointer-events-none disabled:opacity-60 [&_svg]:size-4 [&_svg]:shrink-0',
        size === 'md' && 'h-11 px-5 text-sm',
        size === 'lg' && 'h-13 px-7 text-[0.95rem]',
        variant === 'primary' && 'bg-signal-600 text-white hover:bg-signal-700',
        variant === 'secondary' && 'bg-petrol-900 text-kalk-50 hover:bg-petrol-800',
        variant === 'ghostDark' && 'border border-white/25 text-kalk-50 hover:border-white/45 hover:bg-white/8',
        variant === 'outline' && 'border border-input bg-transparent text-foreground hover:border-petrol-900/40 hover:bg-kalk-100',
        className
    )
}

export const ButtonLink: FC<ButtonLinkProps> = ({ variant, size, className, ...props }): ReactNode => {
    return <Link className={buttonVariants({ variant, size, className })} {...props} />
}

export const Button: FC<ButtonProps> = ({ variant, size, className, type = 'button', ...props }): ReactNode => {
    return <button type={type} className={buttonVariants({ variant, size, className })} {...props} />
}
